


















































































































































































































































import Vue, { VueConstructor } from 'vue';
import {
  defineComponent,
  PropType,
  Ref,
  ref,
  watch,
  nextTick,
  provide,
  useRoute,
  useRouter,
  useContext,
} from '@nuxtjs/composition-api';
import {
  SfSelect, SfSidebar, SfImage, SfButton, SfAccordion, SfSelectOption,
} from '~/components/StorefrontUi';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useUiHelpers, useUiState } from '~/composables';
import { Pagination } from '~/composables/types';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import { addBasePath } from '~/helpers/addBasePath';
import type { SelectedFiltersInterface } from '~/modules/catalog/category/components/filters/useFilters';
import { useFilters } from '~/modules/catalog/category/components/filters/useFilters';
import type { Aggregation, AggregationOption } from '~/modules/GraphQL/types';
import {
  getFilterConfig,
  isFilterEnabled,
} from '~/modules/catalog/category/config/FiltersConfig';
import SelectedFilters from '~/modules/catalog/category/components/filters/FiltersSidebar/SelectedFilters.vue';
import CategoryDropdownFilters from '~/modules/catalog/category/components/filters/CategoryDropdownFilters.vue';
import { rangeFilters } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeFilterInput';
import { sortFiltersByAttributeCode } from '~/helpers/sortFiltersByAttributeCode';
import { SORTED_FILTER_ATTRIBUTE_CODES } from '~/modules/catalog/category/consts';
import CategoryLayoutSwitcher from '~/modules/catalog/category/components/CatalogLayoutSwitcher.vue';
import { useTraverseCategory } from '../../helpers/useTraverseCategory';

export interface UseFiltersProviderInterface {
  selectedFilters: Ref<SelectedFiltersInterface>;
  filters: Ref<Aggregation[]>;
}

export default defineComponent({
  components: {
    CategoryDropdownFilters,
    SelectedFilters,
    SkeletonLoader,
    SfImage,
    SfSelect,
    SfSelectOption,
    SfButton,
    SfSidebar,
    SfAccordion,
    CategoryLayoutSwitcher,
    CheckboxType: () => import(
      '~/modules/catalog/category/components/filters/renderer/CheckboxType.vue'
    ),
    RadioType: () => import(
      '~/modules/catalog/category/components/filters/renderer/RadioType.vue'
    ),
    YesNoType: () => import(
      '~/modules/catalog/category/components/filters/renderer/YesNoType.vue'
    ),
    RangeSliderType: () => import(
      '~/modules/catalog/category/components/filters/renderer/RangeSliderType.vue'
    ) as unknown as Promise<VueConstructor<Vue>>,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    isSortVisible: {
      type: Boolean,
      default: false,
    },
    isFilterVisible: {
      type: Boolean,
      default: false,
    },
    catUid: {
      type: String,
      required: true,
    },
    sortBy: {
      type: Object as PropType<SortingModel>,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    loadedFilters: {
      type: Array as PropType<Aggregation[]>,
      required: true,
    },
  },
  setup(
    props: {
      isSortVisible: boolean;
      isFilterVisible: boolean;
      catUid: string;
      sortBy: SortingModel;
      pagination: Pagination;
      loadedFilters: Aggregation[];
    },
    { emit },
  ) {
    const { changeSorting, changeFilters, clearFilters } = useUiHelpers();
    const removableFilters = ref([]);
    const searchFilter = ref({});
    const newFilter = ref({});
    const filters = ref<Aggregation[]>([]);
    const mainFilters = ref<Aggregation[]>([]);
    const promotedFilters = ref<Aggregation[]>([]);
    const selectedSortBy = ref(props.sortBy.selected);
    const selectedFilter = ref(null);
    const route = useRoute();
    const router = useRouter();
    const {
      app: { i18n },
    } = useContext();

    const {
      toggleFilterSidebar,
      isFilterSidebarOpen,
      toggleSortSidebar,
      isSortSidebarOpen,
    } = useUiState();

    const {
      selectedFilters,
      selectFilter,
      removeFilter,
      isFilterSelected,
      getRemovableFilters,
    } = useFilters();

    const getSearchFilter = () => {
      let query = route.value?.query;
      if (typeof window !== 'undefined') {
        query = router.resolve(
          (window.location.pathname + window.location.search).slice(1),
        ).route.query;
      }
      const searchTerm = query.search;
      return searchTerm
        ? {
          id: 'search',
          label: `${i18n.t('Looking for')}: ${searchTerm}`,
          value: searchTerm,
          name: `Szukasz: ${searchTerm}`,
        }
        : {};
    };
    const getNewFilter = () => {
      let query = route.value?.query;
      if (typeof window !== 'undefined') {
        query = router.resolve(
          (window.location.pathname + window.location.search).slice(1),
        ).route.query;
      }
      const isNewFilter = !!query.new;
      return isNewFilter
        ? {
          id: 'new',
          label: i18n.t('New Products'),
          value: 'new',
          name: 'Nowości',
        }
        : {};
    };
    const removeSearch = () => {
      let query = { ...router.currentRoute.query };
      if (typeof window !== 'undefined') {
        query = router.resolve(
          (window.location.pathname + window.location.search).slice(1),
        ).route.query;
      }
      if (query.search) {
        delete query.search;
        router.replace({ query });
      }
    };

    const { activeCategory } = useTraverseCategory();

    const doChangeSorting = (sort: string) => {
      changeSorting(sort, false);
      emit('reloadProducts');
      if (isSortSidebarOpen.value) {
        toggleSortSidebar();
      }
    };

    const updateRemovableFilters = () => {
      removableFilters.value = getRemovableFilters(
        filters.value,
        selectedFilters.value,
      );
      searchFilter.value = getSearchFilter();
      newFilter.value = getNewFilter();
    };

    const doApplyFilters = () => {
      changeFilters(selectedFilters.value, false);
      updateRemovableFilters();
      emit('reloadProducts');
      emit('close');
      selectedFilter.value = null;
      if (isFilterSidebarOpen.value) {
        toggleFilterSidebar();
      }
    };

    const doRemoveFilter = ({ id, value }: { id: string; value: string }) => {
      if (id === 'search') {
        removeSearch();
      } else {
        removeFilter(id, value);
        changeFilters(selectedFilters.value, false);
      }
      updateRemovableFilters();
      emit('reloadProducts');
      emit('close');
    };

    const doClearFilters = () => {
      clearFilters(false);
      selectedFilters.value = {};
      updateRemovableFilters();
      emit('reloadProducts');
      emit('close');
      selectedFilter.value = null;
      if (isFilterSidebarOpen.value) {
        toggleFilterSidebar();
      }
    };

    watch(
      () => props.isFilterVisible,
      (newValue) => {
        // disable Storefrontt UI's body scroll lock which is launched when :visible prop on SfSidebar changes
        // two next ticks because SfSidebar uses nextTick aswell, and we want to do something after that tick.
        if (newValue) {
          nextTick(() => nextTick(() => clearAllBodyScrollLocks()));
        }
      },
    );

    const getFilterByGroups = (
      filtersArg: Aggregation[],
    ): {
      main: Aggregation[];
    } => {
      const main = [];
      filtersArg.forEach((filter) => {
        main.push(filter);
      });
      return { main };
    };

    const initFilters = () => {
      filters.value = props.loadedFilters.filter((filter) => isFilterEnabled(filter.attribute_code));
      const sortedFilters = sortFiltersByAttributeCode(
        filters.value,
        SORTED_FILTER_ATTRIBUTE_CODES,
      );
      const { main } = getFilterByGroups(sortedFilters);
      mainFilters.value = main;

      // TODO: move this to config
      const PROMOTED_FILTERS_EXCLUSIONS = new Set(rangeFilters);
      const MAX_PROMOTED_FILTERS = 4;
      const filteredPromoFilters = sortedFilters
        .filter(
          (filter) => !PROMOTED_FILTERS_EXCLUSIONS.has(filter.attribute_code),
        )
        .slice(0, MAX_PROMOTED_FILTERS);
      promotedFilters.value = filteredPromoFilters;

      updateRemovableFilters();
    };
    initFilters();

    provide('UseFiltersProvider', {
      isFilterSelected,
      selectedFilters,
      filters,
    });

    const doSelectFilter = ({
      filter,
      value,
    }: {
      filter: Aggregation;
      value: AggregationOption;
    }) => {
      selectFilter(filter, value);
      doApplyFilters();
    };

    const filterAttributeCodesToTranslate = new Set(['price']);
    const getFilterLabel = (filter: Aggregation) => (filterAttributeCodesToTranslate.has(filter.attribute_code)
      ? i18n.t(filter.label)
      : filter.label);

    watch(
      () => props.loadedFilters,
      () => {
        initFilters();
      },
    );
    return {
      activeCategory,
      doSelectFilter,
      selectFilter,
      doApplyFilters,
      doRemoveFilter,
      doClearFilters,
      getFilterConfig,
      selectedFilters,
      promotedFilters,
      filters,
      mainFilters,
      selectedSortBy,
      searchFilter,
      newFilter,
      selectedFilter,
      removableFilters,
      toggleFilterSidebar,
      toggleSortSidebar,
      doChangeSorting,
      changeSorting,
      changeFilters,
      addBasePath,
      getFilterLabel,
    };
  },
});
